import Service, { service } from '@ember/service';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import { TrackedSet } from 'tracked-built-ins';

/**
 * Client for the flow-scoped "sensitive data categories" authoring endpoint
 * (envoy-web v3). Deliberately separate from the field-settings/retention path:
 * category sensitivity lives in its own table, decoupled from the anonymizer's
 * field_settings. Holds the per-flow selected set as the source of truth for
 * the category rows in the Sensitive fields tab.
 *
 * Both the selected set and the saved baseline are TrackedSets, mutated in
 * place. This matters for reactivity: isDirty() reads both, so consumers like
 * form-page's canSave recompute when EITHER changes — including after save(),
 * which updates the baseline (otherwise the Save button/dirty prompt would
 * never clear after a successful save).
 */
interface SensitiveCategoriesResponse {
  available_categories?: string[];
  sensitive_categories?: string[];
}

const endpoint = (flowId: string): string => `/a/visitors/api/v3/flows/${flowId}/sensitive-data-categories`;

export default class SensitiveDataCategoriesService extends Service {
  @service declare ajaxFetch: AjaxFetchService;

  private selectedByFlow: Record<string, TrackedSet<string>> = {};
  private baselineByFlow: Record<string, TrackedSet<string>> = {};
  private availableByFlow: Record<string, TrackedSet<string>> = {};

  private trackedSetFor(store: Record<string, TrackedSet<string>>, flowId: string): TrackedSet<string> {
    let set = store[flowId];
    if (!set) {
      set = new TrackedSet<string>();
      store[flowId] = set;
    }
    return set;
  }

  private selectedSet(flowId: string): TrackedSet<string> {
    return this.trackedSetFor(this.selectedByFlow, flowId);
  }

  private baselineSet(flowId: string): TrackedSet<string> {
    return this.trackedSetFor(this.baselineByFlow, flowId);
  }

  private availableSet(flowId: string): TrackedSet<string> {
    return this.trackedSetFor(this.availableByFlow, flowId);
  }

  private static replaceInPlace(set: TrackedSet<string>, values: Iterable<string>): void {
    set.clear();
    for (const value of values) set.add(value);
  }

  async load(flowId: string): Promise<void> {
    // Route through ajaxFetch so cookieAuth.decorate runs (blanks Authorization,
    // adds X-CSRF-Token on protected-method writes) — matching the sign-in-field
    // saves on this cookie+CSRF /a/visitors path.
    const body = await this.ajaxFetch.request<SensitiveCategoriesResponse>(endpoint(flowId));
    const sensitive = body.sensitive_categories ?? [];
    SensitiveDataCategoriesService.replaceInPlace(this.selectedSet(flowId), sensitive);
    SensitiveDataCategoriesService.replaceInPlace(this.baselineSet(flowId), sensitive);
    // The backend owns the list of categories that can be marked sensitive for
    // this flow; keep it here so the UI has a single source of truth and new
    // categories light up with a backend deploy alone (no client change).
    SensitiveDataCategoriesService.replaceInPlace(this.availableSet(flowId), body.available_categories ?? []);
  }

  // Categories the backend offers for this flow, in backend order. Empty until
  // load() runs (or if it failed) — callers render no category rows in that case.
  availableCategories(flowId?: string): string[] {
    if (!flowId) return [];
    return Array.from(this.availableSet(flowId));
  }

  isSelected(flowId: string, category: string): boolean {
    return this.selectedSet(flowId).has(category);
  }

  setSensitive(flowId: string, category: string, value: boolean): void {
    const selected = this.selectedSet(flowId);
    if (value) {
      selected.add(category);
    } else {
      selected.delete(category);
    }
  }

  toggle(flowId: string, category: string): void {
    this.setSensitive(flowId, category, !this.isSelected(flowId, category));
  }

  isDirty(flowId?: string): boolean {
    if (!flowId) return false;
    const selected = this.selectedSet(flowId);
    const baseline = this.baselineSet(flowId);
    if (selected.size !== baseline.size) return true;
    for (const category of selected) {
      if (!baseline.has(category)) return true;
    }
    return false;
  }

  rollback(flowId: string): void {
    SensitiveDataCategoriesService.replaceInPlace(this.selectedSet(flowId), this.baselineSet(flowId));
  }

  async save(flowId: string): Promise<void> {
    const categories = Array.from(this.selectedSet(flowId));
    // ajaxFetch runs cookieAuth.decorate here: it blanks Authorization and sends
    // X-CSRF-Token on this cookie+CSRF write path (what stops the PATCH 401'ing),
    // and throws typed errors on a non-2xx like the rest of the API.
    await this.ajaxFetch.patch(endpoint(flowId), {
      contentType: 'application/json; charset=utf-8',
      data: { sensitive_categories: categories },
    });
    // Update the baseline in place so isDirty() (and thus canSave) recomputes
    // and the Save button / unsaved-changes prompt clear after a save.
    SensitiveDataCategoriesService.replaceInPlace(this.baselineSet(flowId), categories);
  }
}

declare module '@ember/service' {
  interface Registry {
    'sensitive-data-categories': SensitiveDataCategoriesService;
  }
}
